import React, { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LoadingDots } from '../utilities/Utilities';
import { useAppContext } from '../../Contexts/AppContext';
import { z } from 'zod';
import { EyeOpenIcon, EyeClosedIcon } from '@radix-ui/react-icons';
import OnBoardingShell from '../onboarding-shell/OnBoardingShell';

const RegistrationFormSchema = z
  .object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    email: z.string().email('Invalid email address'),
    // signupType: z.enum(['counsel', 'law-firm']),
    scnNumber: z.string().optional(),
    password: z.string()
      .min(8, 'Password must be at least 8 characters long')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number'),
    confirmPassword: z
      .string()
      .min(8, 'Confirm Password must be at least 8 characters long'),
  })
  // .refine((data) => {
  //   if (data.signupType === 'counsel') {
  //     return (data.scnNumber?.length || 0) >= 9; 
  //   }
  //   return true;
  // }, {
  //   message: 'Invalid SCN Number',
  //   path: ['scnNumber'],
  // })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

const RegistrationForm: React.FC = () => {
  const { handleRegister, togglePasswordVisibility, showPassword } = useAppContext();
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [middleName, setMiddleName] = useState<string>('');
  const [scnNumber, setScnNumber] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    const result = RegistrationFormSchema.safeParse({
      firstName,
      lastName,
      middleName,
      scnNumber,
      email,
      password,
      confirmPassword,
    });

    if (!result.success) {
      setError(result.error.errors.map((err) => err.message).join(', '));
      setIsLoading(false);
      return;
    }

    try {
      const userData = {
        firstName,
        lastName,
        middleName,
        email,
        password,
        scnNumber,
        signupType: 'counsel',
      };
      const response = await fetch('https://backend-kyw8.onrender.com/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });
      const data = await response.json();
      console.log(data);

      if (response.status === 200) {
        navigate('/verify-email', {
          state: { email },
        });
      } else if (response.ok) {
        toast.success('Registration successful!', {
          position: 'top-center',
        });
        navigate(`/otp`, {
          state: { email },
        });
        handleRegister();
      } else if (response.status === 409) {
        setError('Already existing User');
        console.log(data);
      } else {
        setError(data?.error || 'An unexpected error occurred.');
       }
    } catch (error) {
      console.error('Error:', error);
      toast.error('An unexpected error occurred. Please try again later.', {
        position: 'bottom-right',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <OnBoardingShell title='Welcome!' screenText='Register to continue'>
      <div className='md:w-1/2 w-full relative flex flex-col items-center justify-center px-10 m-auto'>
        <h2 className='form-header mb-5 !text-start w-full'>Create Account</h2>
        <form onSubmit={handleSubmit}>
          <div className='flex flex-col lg:flex-row lg:gap-5'>
            <div className='mb-2'>
              <label htmlFor='firstname' className='input-label'>
                First Name <span className='text-red-500'>*</span>
              </label>
              <input
                type='text'
                id='firstname'
                value={firstName}
                placeholder='First Name'
                onChange={(e) => setFirstName(e.target.value)}
                className='input'
              />
            </div>
            <div className='mb-2'>
              <label htmlFor='lastname' className='input-label'>
                Last Name <span className='text-red-500'>*</span>
              </label>
              <input
                type='text'
                id='lastname'
                value={lastName}
                placeholder='Last Name'
                onChange={(e) => setLastName(e.target.value)}
                className='input'
              />
            </div>
          </div>
          <div className='flex flex-row gap-5'>
            <div className='mb-2'>
              <label htmlFor='middlename' className='input-label'>Middle Name</label>
              <input
                type='text'
                id='middlename'
                value={middleName}
                placeholder='Middle Name'
                onChange={(e) => setMiddleName(e.target.value)}
                className='input'
              />
            </div>
             
            <div className='mb-2'>
              <label htmlFor='scnNumber' className='input-label'>
                SCN NO <span className='text-red-500'>*</span>
              </label>
              <input
                type='text'
                id='scnNumber'
                value={scnNumber}
                placeholder='SCN000000'
                onChange={(e) => setScnNumber(e.target.value)}
                className='input'
              />
            </div>

          </div>
          <div className='mb-2'>
            <label htmlFor='email' className='input-label'>
              Email <span className='text-red-500'>*</span>
            </label>
            <input
              type='email'
              id='email'
              value={email}
              placeholder='abcd@gmail.com'
              onChange={(e) => setEmail(e.target.value)}
              className='input'
            />
          </div>
          <div className='mb-2 relative'>
            <label htmlFor='password' className='input-label'>
              Password <span className='text-red-500'>*</span>
            </label>
            <div className='password-input-container'>
              <input
                type={showPassword ? 'text' : 'password'}
                id='password'
                value={password}
                placeholder='Password'
                onChange={(e) => setPassword(e.target.value)}
                className='input pr-16'
              />
              <button
                type='button'
                onClick={() => togglePasswordVisibility('password')}
                className='absolute inset-y-8 right-0 px-3'
              >
                {showPassword ? (
                  <EyeOpenIcon className='h-8' />
                ) : (
                  <EyeClosedIcon className='h-8' />
                )}
              </button>
            </div>
          </div>
          <div className='mb-2 relative'>
            <label htmlFor='confirmPassword' className='input-label'>
              Confirm Password <span className='text-red-500'>*</span>
            </label>
            <div className='password-input-container'>
              <input
                type={showPassword ? 'text' : 'password'}
                id='confirmPassword'
                value={confirmPassword}
                placeholder='Confirm Password'
                onChange={(e) => setConfirmPassword(e.target.value)}
                className='input pr-16'
              />
            </div>
          </div>
          {error && <p className='text-red-500 mt-2'>{error}</p>}
          <button
            type='submit'
            disabled={isLoading}
            className={`button-primary !font-semibold !text-white ${isLoading ? 'py-4' : 'py-2'}`}
          >
            {isLoading ? <LoadingDots /> : 'Register'}
          </button>
          
        </form>
        <div className='mt-4'>
          <Link
            to='/login'
            className='text-center text-blue-500 hover:text-blue-700'
          >
            Already have an account? Login
          </Link>
        </div>
      </div>
    </OnBoardingShell>
  );
};

export default RegistrationForm;
